import { type Identifier, useGetList, useGetMany, useTranslate } from "ra-core";
import { useMemo } from "react";
import { DateField } from "@/components/admin/date-field";

import type { Sale, TaskHistoryEntry } from "../types";

/** Columns worth naming in the trail; `id` never changes and adds only noise. */
const TRACKED_FIELDS = [
  "text",
  "due_date",
  "type",
  "done_date",
  "contact_id",
  "sales_id",
];

/**
 * The audit trail for one task, newest first.
 *
 * Every account manager can edit every other's tasks, so this is what makes a
 * cross-account-manager change attributable. Rows are written by the
 * `log_task_change` trigger and are read-only.
 */
export const TaskHistory = ({ taskId }: { taskId: Identifier }) => {
  const translate = useTranslate();

  const { data: entries, error } = useGetList<TaskHistoryEntry>(
    "task_history",
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: "changed_at", order: "DESC" },
      filter: { task_id: taskId },
    },
    { enabled: taskId != null },
  );

  const actorIds = useMemo(
    () =>
      Array.from(
        new Set(
          (entries ?? [])
            .map((entry) => entry.sales_id)
            .filter((id): id is Identifier => id != null),
        ),
      ),
    [entries],
  );

  const { data: actors } = useGetMany<Sale>(
    "sales",
    { ids: actorIds },
    { enabled: actorIds.length > 0 },
  );

  const actorNameById = useMemo(
    () =>
      new Map(
        actors?.map((actor) => [
          String(actor.id),
          `${actor.first_name} ${actor.last_name}`,
        ]),
      ),
    [actors],
  );

  // The trail is supporting detail, not the point of the dialog: if it cannot
  // be read (demo mode has no trigger to fill it), show nothing rather than an
  // error the user can do nothing about.
  if (error || !entries?.length) return null;

  const describeFields = (entry: TaskHistoryEntry) => {
    const changed = Object.keys(entry.changes ?? {}).filter((field) =>
      TRACKED_FIELDS.includes(field),
    );
    if (!changed.length) return null;
    return changed
      .map((field) =>
        translate(`resources.tasks.fields.${field}`, { _: field }),
      )
      .join(", ");
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
        {translate("resources.tasks.history.title")}
      </p>
      <ul className="flex flex-col gap-1">
        {entries.map((entry) => {
          const actorName =
            (entry.sales_id != null &&
              actorNameById.get(String(entry.sales_id))) ||
            translate("resources.tasks.history.unknown_user");
          const fields = describeFields(entry);

          return (
            <li key={entry.id} className="text-sm text-muted-foreground">
              {entry.operation === "deleted"
                ? translate("resources.tasks.history.deleted_by", {
                    name: actorName,
                  })
                : translate("resources.tasks.history.updated_by", {
                    name: actorName,
                    fields:
                      fields ??
                      translate("resources.tasks.history.other_fields"),
                  })}{" "}
              <DateField
                source="changed_at"
                record={entry}
                showDate
                showTime
                className="inline text-sm text-muted-foreground"
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};
